import { useState, useEffect } from 'react';
import WrapInputLabel from 'common/components/wrap-input-label';
import { Form, Formik } from 'formik';
import { BsTelephone } from 'react-icons/bs';
import { FaRegUser } from 'react-icons/fa';
import { HiOutlineMail } from 'react-icons/hi';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { isEmpty } from 'common/utils/isEmpty';
import * as Yup from 'yup';
import Tracker from 'components/auth/signup/tracker'; // Assuming tracker is generic enough
import AuthLabel from 'common/components/auth-label';
import Button from 'common/components/button';
import FormError from 'common/components/form-error';
import Input from 'common/components/input';
import {
  useConfirmAccountMutation,
  useRegisterMutation,
} from 'redux/api/ciosUsersApi/ciosUsersApi';
import { setCredentials, logout } from 'redux/features/auth/authSlice';
import { Link } from 'common/utils/user-tracker/react-router-dom';
import useUserTracker from 'common/utils/user-tracker/useUserTracker';
import { PhoneNumberInput } from 'common/components/phone-input';
import { ApplyFormField, ApplyFormLayout } from 'common/layouts/apply-form';
import { Flex } from '@chakra-ui/react';
import { clearClaimsDraft } from 'redux/features/claims/claimsSlice';
import { clearPlansDraft } from 'redux/features/plans/plansSlice';
import SelectInput from 'common/components/select-input';

const SignupForm = ({
  property,
  owner,
  propertiesMenu,
  externalOnSubmit,
  isLoading: externalIsLoading,
}) => {
  // Add new props
  const [hasSignupCode, setHasSignupCode] = useState(false);
  const [searchParams] = useSearchParams();
  const userToken = searchParams.get('token');
  const firstNameQueryParam = searchParams.get('firstName');
  const lastNameQueryParam = searchParams.get('lastName');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [register, { isLoading: isRegisterLoading }] = useRegisterMutation(); // Standard register
  const [confirmAccount, { isLoading: isConfirmingAccount }] =
    useConfirmAccountMutation(); // Keep confirm account

  const {
    trackLink,
    trackIdentity,
    trackFieldPopulate,
    trackFormSubmission,
    trackFormSubmissionSuccess,
    trackFormSubmissionError,
  } = useUserTracker();

  useEffect(() => {
    dispatch(logout());
    dispatch(clearClaimsDraft());
    dispatch(clearPlansDraft());
  }, [dispatch]);

  const effectiveIsLoading =
    externalIsLoading || isRegisterLoading || isConfirmingAccount;

  const initialValues = {
    firstName: firstNameQueryParam || '',
    lastName: lastNameQueryParam || '',
    phone: '',
    email: searchParams.get('email') || '',
    signupCode: '',
    ...(owner && propertiesMenu ? { propertyID: '' } : {}),
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={async (values) => {
        // Made onSubmit async
        const { firstName, lastName, email, phone, signupCode } = values;

        if (externalOnSubmit) {
          // If an external submit handler is provided, use it
          await externalOnSubmit({
            firstName,
            lastName,
            email,
            phone,
            signupCode,
            ...(owner && propertiesMenu
              ? { propertyID: values.propertyID }
              : {}),
          }); // Pass all form values
        } else if (!isEmpty(userToken)) {
          // Confirm Account Flow (remains the same)
          // ...
        } else {
          // Regular Signup Flow (remains the same)
          let valueCp = {
            firstName,
            lastName,
            email,
            phone,
            signupCode,
          };
          trackFormSubmission(valueCp);
          register({
            firstName,
            lastName,
            signupCode,
            email,
            phone,
          })
            .unwrap()
            .then(({ data }) => {
              toast.info(data?.message || 'Success');
              trackIdentity(data?.user?.id || 'unknown-id', data?.user || {});
              trackFormSubmissionSuccess(valueCp);
              dispatch(
                setCredentials({
                  isLoggedIn: true,
                  token: data?.token,
                  user: data?.user,
                })
              );
              navigate('/confirm-email');
            })
            .catch((error) => {
              toast.error(error?.data?.msg || 'Registration Failed');
              trackFormSubmissionError(
                valueCp,
                error?.data?.msg || 'Registration Failed'
              );
            });
        }
      }}
      validationSchema={Yup.object({
        // ... validation schema remains the same
        firstName: Yup.string().required('Required'),
        lastName: Yup.string().required('Required'),
        email: Yup.string().email('Invalid email address').required('Required'),
        phone: Yup.string()
          .required('Phone number is required')
          .test('is-valid-phone', 'Phone number is invalid', (value) => {
            return isValidPhoneNumber(value || '');
          }),
        signupCode: Yup.string().optional(),
        propertyID: Yup.string().when(['owner', 'propertiesMenu'], {
          is: (ownerValue, propertiesMenuValue) =>
            !!ownerValue && !!propertiesMenuValue,
          then: () => Yup.string().required('Required'),
          otherwise: () => Yup.mixed().strip(),
        }),
      })}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        isValid,
        setFieldTouched,
        setFieldValue,
        handleSubmit, // Ensure handleSubmit is destructured if Formik's own submit is used
      }) => (
        <div className='px-6 md:px-8 flex flex-col gap-12'>
          <div className='flex gap-3 items-center'>
            <Link to='/login' linkId='signupLoginBtn'>
              <AuthLabel title='Log In' isActive={false} />
            </Link>
            <AuthLabel title='Create Account' />
          </div>
          {/* Pass handleSubmit from Formik to the <Form> component */}
          <Form
            onSubmit={handleSubmit}
            className='flex flex-col items-stretch w-full gap-9'
          >
            <p className='text-4xl font-display tracking-tight'>
              {property?.name ? (
                <span>
                  <span className='text-2xl'>Welcome to</span>
                  <br />
                  {property?.name}
                </span>
              ) : owner?.name ? (
                <span>
                  <span className='text-2xl'>Welcome to</span>
                  <br />
                  {owner?.name}
                </span>
              ) : (
                'Create a New ReLease Account'
              )}
            </p>
            <ApplyFormLayout>
              {/* ... (form fields remain the same) ... */}
              {owner && propertiesMenu ? (
                <div className='mb-2 border-b border-gray-200 pb-7 col-span-2'>
                  <ApplyFormField>
                    <WrapInputLabel isRequired>Community Name</WrapInputLabel>
                    <SelectInput
                      options={propertiesMenu}
                      isSearchable={true}
                      placeholder='Select your community'
                      name='propertyID'
                      value={
                        propertiesMenu.find(
                          (selectedProperty) =>
                            selectedProperty.value === values.propertyID
                        ) || null
                      }
                      onChange={(e) => {
                        setFieldValue('propertyID', e.value);
                      }}
                      onBlur={(e) => {
                        setFieldTouched('propertyID', true);
                      }}
                    />
                    <FormError name='propertyID' />
                  </ApplyFormField>
                </div>
              ) : null}
              <ApplyFormField small>
                <WrapInputLabel isRequired>First Name</WrapInputLabel>
                <Input
                  name='firstName'
                  value={values.firstName}
                  onChange={(e) => {
                    setFieldTouched('firstName');
                    handleChange(e);
                  }}
                  onBlur={handleBlur}
                  placeholderText='First name'
                  Icon={FaRegUser}
                />
                <FormError name='firstName' />
              </ApplyFormField>
              <ApplyFormField small>
                <WrapInputLabel isRequired>Last Name</WrapInputLabel>
                <Input
                  name='lastName'
                  value={values.lastName}
                  onChange={(e) => {
                    setFieldTouched('lastName');
                    handleChange(e);
                  }}
                  onBlur={handleBlur}
                  placeholderText='Last name'
                  Icon={FaRegUser}
                />
                <FormError name='lastName' />
              </ApplyFormField>
              <ApplyFormField>
                <WrapInputLabel isRequired>Email Address</WrapInputLabel>
                <Input
                  name='email'
                  onBlur={handleBlur}
                  onChange={(e) => {
                    setFieldTouched('email');
                    handleChange(e);
                  }}
                  value={values.email}
                  placeholderText='Email address'
                  Icon={HiOutlineMail}
                />
                <FormError name='email' />
              </ApplyFormField>
              <ApplyFormField>
                <WrapInputLabel isRequired>Phone Number</WrapInputLabel>
                <PhoneNumberInput
                  name='phone'
                  value={values.phone}
                  onChange={(e) => {
                    setFieldValue('phone', e);
                  }}
                  onBlur={(e) => {
                    handleBlur(e);
                    trackFieldPopulate('phone');
                  }}
                />
                <FormError name='phone' />
              </ApplyFormField>
            </ApplyFormLayout>
            <Flex className='flex-col gap-3'>
              {hasSignupCode ? (
                <Flex className='col-span-2 pt-6 mb-6 border-t border-gray-200'>
                  <ApplyFormField>
                    <WrapInputLabel>Signup Code:</WrapInputLabel>
                    <Input
                      name='signupCode'
                      value={values.signupCode}
                      onBlur={handleBlur}
                      onChange={(e) => {
                        setFieldTouched('signupCode');
                        handleChange(e);
                      }}
                      disableIcon={true}
                      placeholderText='Signup code'
                      Icon={BsTelephone}
                    />
                  </ApplyFormField>
                </Flex>
              ) : (
                <Button
                  onClick={() => {
                    setHasSignupCode(true);
                    trackLink('Have a signup code?');
                  }}
                  title='Have a signup code?'
                  theme='secondary'
                  className='col-span-2'
                />
              )}
              <Button
                disabled={!isValid || effectiveIsLoading} // Use effectiveIsLoading
                loading={effectiveIsLoading} // Use effectiveIsLoading
                type='submit'
                title='Sign up'
              />
              <Tracker
                values={values}
                errors={errors}
                touched={touched}
                formName='signup'
              />
            </Flex>
          </Form>
        </div>
      )}
    </Formik>
  );
};
export default SignupForm;
